<template>
  <section class="management-container">
    <div class="sidebar-container">
      <document-sidebar :sections="localContents" :active-section-id="activeSectionId" />
    </div>
    <div class="document-container">
      <div class="document-builder" v-if="!loading">
        <input class="document-builder__title" placeholder="Enter Document Title" contenteditable v-model="documentTitle">
    
        <div class="document-builder__header">
          <button @click="addTopLevelSection" class="add-top-level-btn">
            + Add Section
          </button>
          <button @click="handleSave" class="save-btn">
            Save
          </button>
          <button @click="deleteSelectedDocument()" class="delete-btn">
            <icon-selector 
              class="delete-icon"
              icon="Delete"
            />
          </button>
        </div>
    
        <div v-if="localContents.length === 0 && !loading" class="empty-state">
          <div class="empty-state__ghost">
            <div class="ghost-section">
              <div class="ghost-section__content">
                <div class="ghost-title"></div>
                <div class="ghost-text"></div>
                <div class="ghost-text"></div>
                <div class="ghost-text"></div>
              </div>
            </div>
            <div class="ghost-section">
              <div class="ghost-section__content">
                <div class="ghost-title"></div>
                <div class="ghost-text"></div>
                <div class="ghost-text"></div>
              </div>
            </div>
            <div class="ghost-section">
              <div class="ghost-section__content">
                <div class="ghost-title"></div>
                <div class="ghost-text"></div>
              </div>
            </div>
          </div>
          <p class="empty-state__text">Add sections to begin</p>
        </div>
        <transition-group name="list">
            <document-section
              v-for="(section, index) in localContents"
              :id="section.id"
              :key="section.id"
              :section="section"
              :depth="0"
              :index="index"
              :is-first="index === 0"
              :is-last="index === localContents.length - 1"
              @add-child="addChildSection"
              @move="moveSection"
              @remove="removeSection"
            />
        </transition-group>
      </div>
      <div v-if="loading">
        <div class="loader">
          <spinner />
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { useDocuments } from "../../composables/useDocuments";
import { defineComponent, ref } from "vue";
import Spinner from "../Spinner/Spinner.vue"
import DocumentSidebar from "./DocumentSidebar.vue";
import IconSelector from "@/components/Library/IconSelector";


export default {
  name: "DocumentManagement",
  
  components: {
    DocumentSection: () => import("./DocumentSection.vue"),
    Spinner,
    DocumentSidebar,
    IconSelector
  },

  data() {
    return {
      localContents: [],
      localCurrentDocument: [],
      loading: true
    }
  },

  computed: {
    documentTitle: {
      get() {
        return this.$store.state.documentation.currentDocumentTitle;
      },
      set(value) {
        this.$store.state.documentation.currentDocumentTitle = value;
      }
    },
    activeSectionId() {
      return this.$store.state.documentation.activeSectionId;
    }
  },

  created() {
    if (!this.$store.state.documentation.isCreatingNewDocument) {
      this.getDocumentSingle();
    } else {
      this.loading = false
    }
  },
    
  setup() {
    const { getDocument, currentDocument, patchDocument, deleteDocument, postDocument } = useDocuments();
    async function getDocumentSingle() {
      const id = this.$route.params.id;
      this.loading = true;
      this.$store.commit("startLoading")
      try {
        await getDocument(id);
        this.$store.state.documentation.currentDocumentId = id;
        this.$store.state.documentation.currentDocument = currentDocument.value.data.data.attributes.documentContents;

        const documentContents = currentDocument.value.data.data.attributes.documentContents;
        const parsedDocument = JSON.parse(documentContents);
        this.localCurrentDocument = parsedDocument;
        this.initializeContents();
      } catch (error) {
        console.error("Error parsing document contents:", error);
      } finally {
        this.loading = false;
        this.$store.commit("completeLoading")
      }
    }

    async function deleteSelectedDocument() {
      const id = this.$route.params.id;
      if (confirm("Are you sure you want to delete this document?")) {
        await deleteDocument(id)
        this.$router.push("/documents");
      }
    }

    return {
      currentDocument,
      getDocumentSingle,
      patchDocument,
      deleteSelectedDocument,
      deleteDocument,
      postDocument
    }
  },

  methods: {
    initializeContents() {
      try {
        const contents = this.localCurrentDocument
        this.localContents = this.initializeIds(contents);
      } catch (e) {
        this.localContents = this.initializeIds([{
          sectionTitle: "New Section",
          content: "<p>Section content</p>",
          children: []
        }]);
        console.log("error", e)
      }
    },

    generateId() {
      return this.$uuid.v1();
    },

    initializeIds(content, parentId = null) {
      return content.map(section => {
        const newId = section.id || this.generateId();
        return {
          ...section,
          id: newId,
          parentId,
          children: section.children && section.children.length ? this.initializeIds(section.children, newId) : []
        };
      });
    },

    findSection(sections, id) {
      for (const section of sections) {
        if (section.id === id) return section;
        if (section.children && section.children.length) {
          const found = this.findSection(section.children, id);
          if (found) return found;
        }
      }
      return null;
    },

    getParentChildrenArray(id) {
      const section = this.findSection(this.localContents, id);
      if (!section) return this.localContents;
      if (!section.parentId) return this.localContents;
      const parent = this.findSection(this.localContents, section.parentId);
      return parent.children || this.localContents;
    },

    addTopLevelSection() {
      const id = this.generateId()
      const newSection = {
        id: id,
        sectionTitle: "New Section",
        content: "",
        parentId: null,
        children: []
      };
      this.localContents.push(newSection);
      this.$store.state.documentation.currentDocument = JSON.stringify(this.localContents)
      this.$store.commit("setCurrentSectionId", id)
    },

    addChildSection(parentId) {
      const id = this.generateId();
      const newSection = {
        id: id,
        sectionTitle: "New Subsection",
        content: "<p>Subsection content</p>",
        parentId,
        children: []
      };
      this.$store.commit("setCurrentSectionId", id);

      const parent = this.findSection(this.localContents, parentId);
      if (parent) {
        if (!Array.isArray(parent.children)) {
          this.$set(parent, "children", []);
        }
        parent.children.push(newSection);
      }

      this.$store.state.documentation.currentDocument = JSON.stringify(this.localContents);
    },

    moveSection({ id, direction }) {
      const siblings = this.getParentChildrenArray(id);
      const currentIndex = siblings.findIndex(s => s.id === id);
      const newIndex = currentIndex + direction;

      if (newIndex >= 0 && newIndex < siblings.length) {
        const [item] = siblings.splice(currentIndex, 1);
        siblings.splice(newIndex, 0, item);
      }
    },

    removeSection(id) {
      const siblings = this.getParentChildrenArray(id);
      const index = siblings.findIndex(s => s.id === id);
      if (index !== -1) {
        siblings.splice(index, 1);
      }
    },

    updateSection({id, field, value}) {
      const section = this.findSection(this.localContents, id);
      if (section) {
        this.$set(section, field, value);
      }
    },

    cleanupSectionData(sections) {
      return sections.map(section => {
        const cleanSection = {
          sectionTitle: section.sectionTitle || "",
          content: section.content || "",
        };
        
        if (section.children && section.children.length) {
          cleanSection.children = this.cleanupSectionData(section.children);
        } else {
          cleanSection.children = [];
        }
        
        return cleanSection;
      });
    },

    transformToArray(data) {
      if (data && typeof data === "object") {
        return [data];
      }
      return [];
    },

    handleSave() {
      if (this.$store.state.documentation.isCreatingNewDocument) {
        const cleanedData = this.cleanupSectionData(this.localContents);
        // TODO: need to postDoc here as well 
        const currentDocumentTitle = this.$store.state.documentation.currentDocumentTitle
        this.postDocument(currentDocumentTitle, JSON.stringify(this.localContents))
      } else {
        const cleanedData = this.cleanupSectionData(this.localContents);
        const documentData = {
          type: "gamechanger-document",
          attributes: {
            documentTitle: this.documentTitle,
            documentContents: cleanedData
          }
        };  
        const stringifiedData = JSON.stringify(documentData);
        const currentDocId = this.$store.state.documentation.currentDocumentId
        const currentDocumentTitle = this.$store.state.documentation.currentDocumentTitle
        const parsedCurrentDoc = JSON.parse(this.$store.state.documentation.currentDocument) || null
        this.patchDocument(currentDocId, currentDocumentTitle, JSON.stringify(this.localContents))
      }
    }
  },

  watch: {
    activeSectionId(newId) {
      const sectionElement = document.getElementById(newId);
      if (sectionElement) {
        sectionElement.scrollIntoView({ behavior: "smooth" });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.management-container {
  display: flex;
  width: 100%;
  height: 100vh;
}

.sidebar-container {
  flex: 1.5;
  overflow-y: auto;
  position: sticky;
  top: 0;
  height: 100vh;
}

.document-container {
  width: 100%;
  flex: 8;
  overflow-y: auto;
  height: 100vh;
}

.document-builder {
  padding: 56px;
  margin: 0 auto;
  overflow: hidden;

  &__title {
    border-bottom: 2px solid #eee;
    margin-bottom: 20px;
    font-weight: 600;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 24px;

    &:focus {
      color: none;
      outline: none;
    }
  }

  .add-top-level-btn {
    background: #4CAF50;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
  }

  .save-btn {
    background: $purple;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    margin-left: 8px;
  }

  &__header {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin: 12px 0;
  }

  .empty-state {
    text-align: center;
    color: #999;
    margin-top: 50px;

    &__ghost {
      display: flex;
      justify-content: center;
      margin-bottom: 20px;

      .ghost-section {
        background: #f5f5f5;
        border-radius: 4px;
        margin: 0 10px;
        padding: 20px;
        width: 200px;

        &__content {
          .ghost-title {
            height: 20px;
            background: #ddd;
            margin-bottom: 10px;
          }

          .ghost-text {
            height: 10px;
            background: #eee;
            margin-bottom: 5px;
          }
        }
      }
    }

    &__text {
      font-size: 16px;
      color: #666;
    }
  }
}

.loader {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 500px;
}

.delete-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 7px 12px;
    background: rgb(255, 14, 14);
    color: white !important;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    margin-left: 8px;
  }

  .delete-icon {
    width: 18px;
  }
</style>
